import { useState, useRef, useCallback, useEffect } from 'react'
import { Search, X, Building2 } from 'lucide-react'
import { searchCompanies, createCompany } from '../../hooks/useCompanies'
import { useConnectionRecovered } from '../../hooks/useConnectionRecovered'
import { writeErrorMessage } from '../../lib/queuedWrite'

/**
 * Company search field that also offers to create a new company if not found.
 *
 * Props:
 *   value        – { id, name } or null
 *   onChange     – (value: { id, name } | null) => void
 *   placeholder  – input placeholder text
 *   compact      – smaller variant for inline use
 */
export default function CompanySearchOrCreate({ value, onChange, placeholder = 'Search companies…', compact = false }) {
  const [input, setInput] = useState(value?.name || '')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)
  const pendingSelectRef = useRef(false)
  const timerRef = useRef(null)

  // Sync input when value prop changes externally (e.g. AI auto-fill)
  useEffect(() => {
    if (value?.name && value.name !== input) {
      setInput(value.name)
      setResults([])
      pendingSelectRef.current = true
    }
  }, [value?.name])

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return }
    setSearching(true)
    setError(null)
    try {
      const r = await searchCompanies(q)
      setResults(r)
    } catch (err) {
      console.error('CompanySearchOrCreate search error:', err)
      setError('Search failed')
      setResults([])
    } finally {
      setSearching(false)
    }
  }, [])

  useConnectionRecovered(() => { if (input.trim().length >= 2) doSearch(input) })

  const handleInput = (val) => {
    setInput(val)
    setError(null)
    if (!val.trim()) {
      onChange(null)
      setResults([])
      return
    }
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => doSearch(val), 250)
  }

  const handleSelect = (item) => {
    pendingSelectRef.current = true
    setResults([])
    setInput(item.name)
    onChange({ id: item.id, name: item.name })
  }

  const handleCreateNew = async () => {
    const trimmed = input.trim()
    if (!trimmed) return

    setCreating(true)
    setError(null)
    try {
      const newCompany = await createCompany({ name: trimmed })
      setInput(newCompany.name)
      setResults([])
      pendingSelectRef.current = true
      onChange({ id: newCompany.id, name: newCompany.name })
    } catch (err) {
      console.error('CompanySearchOrCreate create error:', err)
      setError(writeErrorMessage(err, 'Failed to create company'))
    } finally {
      setCreating(false)
    }
  }

  const handleClear = () => {
    setInput('')
    setResults([])
    setError(null)
    onChange(null)
  }

  const handleBlur = () => {
    setTimeout(() => {
      setResults([])
      if (pendingSelectRef.current) { pendingSelectRef.current = false; return }
    }, 250)
  }

  const inputClass = compact
    ? 'input-field text-xs pr-7'
    : 'input-field pr-8'

  return (
    <div className="relative">
      <div className="relative">
        <input
          className={inputClass}
          value={input}
          onChange={e => handleInput(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
        />
        {value?.id ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-warm-400 hover:text-red-500 transition-colors"
          >
            <X size={compact ? 11 : 13} />
          </button>
        ) : (
          <Search size={compact ? 11 : 13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-warm-400 pointer-events-none" />
        )}
      </div>

      {/* Dropdown results */}
      {(results.length > 0 || (input.trim().length >= 2 && !searching && !value?.id)) && (
        <div className="absolute top-full mt-1 w-full bg-white border border-warm-300 rounded-lg shadow-lg z-30 overflow-hidden max-h-52 overflow-y-auto">
          {results.map(item => (
            <button key={item.id} type="button"
              className="w-full text-left px-3 py-2 hover:bg-warm-50 text-sm text-warm-800 transition-colors"
              onMouseDown={() => handleSelect(item)}>
              {item.name}
              {item.company_type && <span className="text-warm-400 ml-1.5">— {item.company_type}</span>}
            </button>
          ))}
          {/* Create new company option */}
          {input.trim().length >= 2 && !results.some(r =>
            r.name.toLowerCase() === input.trim().toLowerCase()
          ) && (
            <button
              type="button"
              onMouseDown={handleCreateNew}
              disabled={creating}
              className="w-full text-left px-3 py-2 text-sm text-navy-600 hover:bg-navy-50 transition-colors flex items-center gap-1.5 border-t border-warm-200"
            >
              <Building2 size={12} />
              {creating ? 'Creating…' : `Create "${input.trim()}" as new company`}
            </button>
          )}
        </div>
      )}

      {/* Status indicators */}
      {searching && (
        <p className="text-[10px] text-warm-400 mt-0.5">Searching…</p>
      )}
      {error && (
        <p className="text-[10px] text-red-500 mt-0.5">{error}</p>
      )}
      {value?.id && !searching && !compact && (
        <p className="text-[10px] text-navy-500 mt-0.5">Linked to company</p>
      )}
    </div>
  )
}
